import React, { useMemo } from 'react';
import { AlertTriangle, ArrowDown, CheckCheck, ListTree, Undo2 } from 'lucide-react';
import { contaDaVerificare, dividiInSezioni } from '../../utils/verbaleSezioni';

interface VerbaleSezioniProps {
  contenuto: string;
  sezioniRiviste: string[];
  /** «Vai alla sezione»: indice della sezione (= indice del suo `<h2>` nel foglio). */
  onVai: (indice: number) => void;
  /** Spunta «rivista»: assente ⇒ solo i collegamenti alle sezioni (sola lettura). */
  onSegna?: (chiave: string, rivista: boolean) => void;
  /** «Segna tutte» / «Togli tutte»: le chiavi delle sezioni attuali, in un solo salvataggio. */
  onSegnaTutte?: (chiavi: string[], rivista: boolean) => void;
  /** Segni [da verificare]: senza indice il prossimo, con l'indice il primo di quella sezione. */
  onVaiDaVerificare?: (indiceSezione?: number) => void;
}

/** Riga della revisione sotto la barra del foglio: una casella «rivista» per sezione. */
const VerbaleSezioni: React.FC<VerbaleSezioniProps> = ({ contenuto, sezioniRiviste, onVai, onSegna, onSegnaTutte, onVaiDaVerificare }) => {
  const sezioni = useMemo(
    () => dividiInSezioni(contenuto).filter((s) => s.chiave !== 'preambolo'),
    [contenuto]
  );
  const riviste = useMemo(() => new Set(sezioniRiviste), [sezioniRiviste]);
  const conteggio = sezioni.filter((s) => riviste.has(s.chiave)).length;
  const tutte = sezioni.length > 0 && conteggio === sezioni.length;
  const daVerificareTotale = useMemo(() => contaDaVerificare(contenuto), [contenuto]);

  if (sezioni.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
        <ListTree className="h-3.5 w-3.5" aria-hidden="true" />
        Usa i titoli H2 per dividere il verbale in sezioni.
      </p>
    );
  }

  if (!onSegna) {
    return (
      <nav aria-label="Sezioni del verbale">
        <ul className="flex flex-wrap gap-1.5">
          {sezioni.map((s, i) => (
            <li key={s.chiave}>
              <button
                type="button"
                onClick={() => onVai(i)}
                className="max-w-[14rem] truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {s.titolo || 'Senza titolo'}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return (
    <section aria-label="Revisione delle sezioni">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-semibold text-slate-700 dark:text-gray-200" aria-live="polite">
          Revisione: {conteggio} di {sezioni.length} {sezioni.length === 1 ? 'sezione rivista' : 'sezioni riviste'}
        </span>
        {daVerificareTotale > 0 && (
          <button
            type="button"
            onClick={() => onVaiDaVerificare?.()}
            disabled={!onVaiDaVerificare}
            title="Porta al prossimo punto [da verificare] e lo seleziona: scrivi il dato giusto al suo posto"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {daVerificareTotale === 1 ? '1 punto da verificare' : `${daVerificareTotale} punti da verificare`}
            <span className="font-normal underline">Vai al prossimo</span>
          </button>
        )}
        <ul className="flex flex-wrap gap-1.5">
          {sezioni.map((s, i) => {
            const fatta = riviste.has(s.chiave);
            const titolo = s.titolo || 'Senza titolo';
            const idCasella = `verbale-rivista-${s.chiave}`;
            return (
              <li
                key={s.chiave}
                className={`inline-flex max-w-full items-center rounded-full border text-xs transition-colors ${
                  fatta
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-slate-200 bg-white text-slate-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                <label htmlFor={idCasella} className="inline-flex min-w-0 cursor-pointer items-center gap-1.5 py-1 pl-2.5 pr-1">
                  <input
                    id={idCasella}
                    type="checkbox"
                    checked={fatta}
                    onChange={() => onSegna(s.chiave, !fatta)}
                    className="h-3.5 w-3.5 flex-shrink-0 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-gray-500 dark:bg-gray-900"
                  />
                  <span className="max-w-[14rem] truncate font-medium">{titolo}</span>
                </label>
                {contaDaVerificare(s.html) > 0 && (
                  <button
                    type="button"
                    onClick={() => onVaiDaVerificare?.(i)}
                    aria-label={`${contaDaVerificare(s.html)} da verificare in «${titolo}»: vai al primo`}
                    title="Punti [da verificare] in questa sezione: vai al primo"
                    className="ml-0.5 inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center gap-0.5 rounded-full bg-amber-400 px-1.5 text-xs font-bold leading-none text-amber-950 hover:bg-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    {contaDaVerificare(s.html)}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onVai(i)}
                  aria-label={`Vai alla sezione «${titolo}»`}
                  title="Vai alla sezione"
                  className="mr-1 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                >
                  <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
        {onSegnaTutte && (
          <button
            type="button"
            onClick={() => onSegnaTutte(sezioni.map((s) => s.chiave), !tutte)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {tutte
              ? <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
              : <CheckCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />}
            {tutte ? 'Togli tutte' : 'Segna tutte come riviste'}
          </button>
        )}
      </div>
      <p className="mt-1.5 text-xs text-slate-500 dark:text-gray-400">
        Spunta una sezione dopo averla riletta: il controllo prima della pubblicazione lo verifica.
        {daVerificareTotale > 0 && ' I punti [da verificare] sono evidenziati in giallo nel foglio: completali o togli il segno.'}
      </p>
    </section>
  );
};

export default VerbaleSezioni;
